import { useSnapshot } from 'valtio';

import { saleorAuthClient } from '@/graphql/client';
import type { CountryCode } from '@/graphql/saleor/api';
import { EVENT_NAME } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import type { WishlistState } from '@/states/wishlistState';
import { wishListState } from '@/states/wishlistState';
import type { SkuWishlistMap, WishlistItem } from '@/views/WishListView/types';
import {
  addItemsToServerWishlistByCountry,
  getLocalWishlistByCountry,
  getServerWishlistByCountry,
  updateLocalWishlistByCountry,
} from '@/views/WishListView/utils';

import { useGoogleAnalytics } from './useGoogleAnalytics';

export interface WishlistProductProps {
  price: number | string | undefined;
  sku: string | null | undefined;
  theCurrency: string | undefined;
  type: string;
}
export interface ProductFavoriteProps {
  country?: CountryCode;
  price?: number | undefined;
  sku?: string | null | undefined;
  theCurrency?: string | undefined;
  type?: string;
  wishlist?: WishlistProductProps[];
}

export const useWishList = ({ countryCode }: { countryCode: CountryCode }) => {
  const { sendEcommerceEventBySkus } = useGoogleAnalytics();
  const snapshot = useSnapshot<WishlistState>(wishListState);
  const { isHighlighted } = snapshot;

  const getWishListForAll = async () => {
    const localWishlist = getLocalWishlistByCountry(countryCode);
    const serverWishlist = await getServerWishlistByCountry(countryCode);
    const wishlist = { ...localWishlist, ...serverWishlist };

    updateLocalWishlistByCountry(countryCode, wishlist);

    return wishlist;
  };

  /**
   * Combine the customer's wishlist for the current country from local and the server,
   * then sync the merged wishlist to both local and the server.
   */
  const mergeWishlist = async () => {
    const localWishlist = getLocalWishlistByCountry(countryCode);
    const serverWishlist = await getServerWishlistByCountry(countryCode);
    const wishlist = { ...localWishlist, ...serverWishlist };

    updateLocalWishlistByCountry(countryCode, wishlist);

    const serverMissingItems: SkuWishlistMap = {};
    Object.keys(localWishlist).forEach((sku) => {
      if (!serverWishlist[sku]) {
        serverMissingItems[sku] = localWishlist[sku];
      }
    });

    if (Object.keys(serverMissingItems).length > 0) {
      await addItemsToServerWishlistByCountry(countryCode, serverMissingItems);
    }
  };

  /**
   * Check if the product item exists in local wishlist of current country
   * @param sku
   */
  const isItemInLocalWishlist = (sku: string) => {
    const localWishlist = getLocalWishlistByCountry(countryCode);
    return !!(sku && sku in localWishlist);
  };

  const addItemToServerWishlist = async (item: WishlistItem) => {
    return await addItemsToServerWishlistByCountry(countryCode, {
      [item.sku]: item,
    });
  };

  const addItemToLocalWishlist = (item: WishlistItem) => {
    const sku = item.sku;
    const prevLocalWishlist = getLocalWishlistByCountry(countryCode);
    if (!isItemInLocalWishlist(sku)) {
      const newLocalWishlist = {
        ...prevLocalWishlist,
        [sku]: item,
      };

      updateLocalWishlistByCountry(countryCode, newLocalWishlist);

      if (sku) {
        sendEcommerceEventBySkus({
          event: EVENT_NAME.ADD_TO_WISHLIST,
          skus: [sku],
        });
      }
    }
  };

  const removeItemFromServerWishlist = async (sku?: string) => {
    try {
      if (sku) {
        await saleorAuthClient
          .fetchWithAuth(
            `${paths.api.wishlist}/${countryCode}/${sku}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              cache: 'no-store',
            },
            { allowPassingTokenToThirdPartyDomains: true },
          )
          .then((res) => res.json());
      }
    } catch (error) {
      //TODO add error toast
    }
  };

  const removeItemFromLocalWishlist = (sku: string) => {
    const localWishlist = getLocalWishlistByCountry(countryCode);
    delete localWishlist[sku];
    updateLocalWishlistByCountry(countryCode, localWishlist);
  };
  return {
    isHighlighted,
    getWishListForAll,
    isItemInLocalWishlist,
    addItemToLocalWishlist,
    removeItemFromLocalWishlist,
    addItemToServerWishlist,
    removeItemFromServerWishlist,
    mergeWishlist,
  };
};
